/**
 * Which auth a saved request is really sent with, and where it comes from —
 * for `request_read` and `request_diagnose` (card 9334908f / MCP 4).
 *
 * The app resolves `inherit` request → nearest folder → … → collection
 * (`RequestBuilder.js` send path and Run collection, both through
 * `utils/requestAuth.resolveEffectiveAuthSource`). This module does NOT
 * re-implement that walk: it takes the folder records from
 * `utils/collectionFolders.getEnclosingFolders` (the walk behind
 * `getFolderAuthChain`) and hands their auth blocks to the SAME resolver, so
 * what an agent is told and what Send does are one code path.
 *
 * Every auth block leaves through `projectAuth` — type, field NAMES, the
 * `{{variables}}` it is wired to — never a credential value.
 *
 * Request kinds the resolver does not apply to are reported honestly rather
 * than as HTTP: an SSE request sends only its OWN bearer token (`auth.token`)
 * and never inherits; gRPC and WebSocket requests send no auth block at all
 * (their credentials, if any, are in gRPC metadata / WebSocket headers).
 */
import { getEnclosingFolders } from '../utils/collectionFolders';
import { resolveEffectiveAuthSource } from '../utils/requestAuth';
import { requestKind } from './protocolProjection';
import { projectAuth } from './requestProjection';

const INHERIT = Object.freeze({ type: 'inherit' });

function authBlockOf(folder) {
  return folder?.auth && typeof folder.auth === 'object' ? folder.auth : null;
}

/**
 * `{ folderAuthChain, effectiveAuthFrom, effectiveAuthType, effectiveAuthFolderId,
 *    effectiveAuth, authAppliesToType }`.
 *
 *   * `folderAuthChain` — every folder enclosing the request, NEAREST FIRST,
 *     `{folderId, name, auth}` with `auth` projected (`inherit` when the
 *     folder sets none, which is how the app stores "Inherit from parent").
 *   * `effectiveAuthFrom` — `request` | `folder` | `collection` | `none`.
 *     A folder explicitly set to No Auth stops the walk: that is `folder` with
 *     `effectiveAuthType: 'noauth'`, exactly as the app sends it.
 *   * `effectiveAuth` — the winning block, projected from the STORED record
 *     (not the normalised one, so defaults do not show up as "fields set").
 */
export function authInheritanceOf(request, collection) {
  const folders = getEnclosingFolders(collection, request?.folder_id ?? null);
  const folderAuthChain = folders.map((folder) => ({
    folderId: String(folder.id),
    name: String(folder.name || ''),
    auth: projectAuth(authBlockOf(folder) || INHERIT),
  }));
  const kind = requestKind(request);

  if (kind === 'grpc' || kind === 'websocket') {
    return {
      folderAuthChain,
      effectiveAuthFrom: 'none',
      effectiveAuthType: 'noauth',
      effectiveAuthFolderId: null,
      effectiveAuth: projectAuth({ type: 'noauth' }),
      authAppliesToType: false,
    };
  }

  if (kind === 'sse') {
    const own = request?.auth && typeof request.auth === 'object' ? request.auth : {};
    const sends = own.type === 'bearer' && typeof own.token === 'string' && own.token.trim() !== '';
    return {
      folderAuthChain,
      effectiveAuthFrom: sends ? 'request' : 'none',
      effectiveAuthType: sends ? 'bearer' : 'noauth',
      effectiveAuthFolderId: null,
      effectiveAuth: sends ? projectAuth(own) : projectAuth({ type: 'noauth' }),
      authAppliesToType: true,
    };
  }

  const collectionAuth = collection?.settings?.collection_auth;
  const resolved = resolveEffectiveAuthSource(
    request?.auth,
    folders.map(authBlockOf),
    collectionAuth
  );
  let source = { type: 'noauth' };
  let folderId = null;
  if (resolved.from === 'request') source = request?.auth;
  else if (resolved.from === 'collection') source = collectionAuth;
  else if (resolved.from === 'folder') {
    const folder = folders[resolved.folderIndex];
    source = authBlockOf(folder);
    folderId = String(folder.id);
  }
  const projected = projectAuth(source);
  return {
    folderAuthChain,
    effectiveAuthFrom: resolved.from,
    effectiveAuthType: resolved.auth.type,
    effectiveAuthFolderId: folderId,
    // The type is the resolver's (a stored block with no `type` normalises);
    // the field names and wiring are the stored block's.
    effectiveAuth: { ...projected, type: resolved.auth.type },
    authAppliesToType: true,
  };
}
